import BaseEntity from '../base/BaseEntity';
import User from '../user/User';
import FileUtil from '../../util/FileUtil';
import Filter from '../base/filter/Filter';
import InputFilter from '../base/filter/InputFilter';

export interface SpaceFormValues {
  name: string;
  sizeLimit: number;
  totalSizeLimit: number;
  uuid: string | null;
  deptIds?: string[]; // 新增：绑定的部门ID列表
  autoSyncDeptMembers?: boolean; // 新增：是否自动同步部门成员
}

export default class Space extends BaseEntity {
  name: string | null = null;
  totalSize: number = 0;
  sizeLimit: number = 0;
  totalSizeLimit: number = 0;
  userUuid: string | null = null;
  user: User | null = null;
  type: 'PRIVATE' | 'SHARED' | null = null;
  
  // 新增：部门绑定相关字段
  deptIds: string[] = []; // 绑定的部门ID列表
  deptNames: string[] = []; // 绑定的部门名称列表（用于显示）
  autoSyncDeptMembers: boolean = false; // 是否自动同步部门成员

  constructor(reactComponent?: React.Component) {
    super(reactComponent);
  }

  getTAG(): string {
    return 'space';
  }

  getFilters(): Filter[] {
    return [
      ...super.getFilters(),
      new InputFilter('空间名称', 'name'),
      new InputFilter('空间类型', 'type'),
    ];
  }

  assign(obj: any) {
    super.assign(obj);
    this.assignEntity('user', User);
    
    // 处理部门相关字段
    if (obj.deptIds !== undefined) {
      this.deptIds = Array.isArray(obj.deptIds) ? obj.deptIds : 
                    (obj.deptIds ? [obj.deptIds] : []);
    }
    
    if (obj.deptNames !== undefined) {
      this.deptNames = Array.isArray(obj.deptNames) ? obj.deptNames : 
                      (obj.deptNames ? [obj.deptNames] : []);
    }
    
    if (obj.autoSyncDeptMembers !== undefined) {
      this.autoSyncDeptMembers = obj.autoSyncDeptMembers;
    }
  }

  getForm(): SpaceFormValues {
    return {
      name: this.name!,
      sizeLimit: this.sizeLimit,
      totalSizeLimit: this.totalSizeLimit,
      uuid: this.uuid ? this.uuid : null,
      deptIds: this.deptIds || [],
      autoSyncDeptMembers: this.autoSyncDeptMembers || false,
    };
  }

  getHumanizeTotalSize() {
    return FileUtil.humanFileSize(this.totalSize);
  }

  getHumanizeSizeLimit() {
    return FileUtil.humanFileSize(this.sizeLimit);
  }

  getHumanizeTotalSizeLimit() {
    return FileUtil.humanFileSize(this.totalSizeLimit);
  }

  getUsedPercent() {
    if (this.totalSizeLimit === 0) {
      return 0;
    }
    return (this.totalSize / this.totalSizeLimit) * 100;
  }

  // 新增：检查是否为部门绑定空间
  isDeptBased(): boolean {
    return this.deptIds && this.deptIds.length > 0;
  }

  // 新增：获取部门显示名称
  getDeptDisplayNames(): string {
    if (!this.deptNames || this.deptNames.length === 0) {
      return '未绑定部门';
    }
    
    // 显示前3个部门名称，超过3个显示"等X个部门"
    const displayNames = this.deptNames.slice(0, 3);
    if (this.deptNames.length <= 3) {
      return displayNames.join('，');
    } else {
      return `${displayNames.join('，')} 等${this.deptNames.length}个部门`;
    }
  }

  // 新增：获取部门数量
  getDeptCount(): number {
    return this.deptIds ? this.deptIds.length : 0;
  }

  // 新增：检查是否绑定指定部门
  hasDept(deptId: string): boolean {
    if (!this.deptIds || this.deptIds.length === 0) {
      return false;
    }
    return this.deptIds.includes(deptId);
  }

  // 新增：添加部门
  addDept(deptId: string, deptName: string): void {
    if (!this.deptIds) {
      this.deptIds = [];
      this.deptNames = [];
    }
    
    // 避免重复添加
    if (!this.deptIds.includes(deptId)) {
      this.deptIds.push(deptId);
      this.deptNames.push(deptName);
    }
  }

  // 新增：移除部门
  removeDept(deptId: string): void {
    if (!this.deptIds || this.deptIds.length === 0) {
      return;
    }
    
    const index = this.deptIds.indexOf(deptId);
    if (index !== -1) {
      this.deptIds.splice(index, 1);
      this.deptNames.splice(index, 1);
    }
  }

  // 新增：清空绑定的部门
  clearDepts(): void {
    this.deptIds = [];
    this.deptNames = [];
  }
}